<script setup lang="ts">
/**
 * The org's active notices, as a band across the top of the app shell.
 *
 * An admin writes a guidance article, marks it a notice and activates it; every member of the
 * organization sees it above every /app route until they close it. Closing it is per user — the
 * dismiss endpoint writes a row keyed by (user, article), so one lawyer clearing the banner does not
 * clear it for the firm.
 *
 * It sits with the service banners under `AppBar` rather than on the welcome page, because a notice
 * is an org-wide statement and a member who lands straight on a case would otherwise never see it.
 *
 * **Content is rendered as TEXT, never as HTML.** It is written by a human in the org and read by
 * everyone else in the org, which is precisely the stored-XSS shape: one admin account (or one
 * compromised one) would otherwise own every session in the firm. `formatMessage()` sanitises, but
 * it exists for model output and reaching for it here buys markdown in exchange for a much larger
 * surface; the authoring UI in settings/rules.vue is a plain textarea and shows the same content the
 * same way. `whitespace-pre-line` keeps the author's line breaks, which is the only formatting a
 * textarea actually offers.
 *
 * **It fails silently.** No error state, no retry: see `useGuidanceNotices` for why an announcement
 * that cannot be fetched is better as nothing than as a red box across the top of the app.
 */
import { Megaphone, X } from 'lucide-vue-next'
// Imported by path rather than left to auto-import: `isLongNotice` is a plain export beside the
// composable, and Nuxt only auto-imports the `use*` half — so taking both from the same import keeps
// the two ends of one module together.
import { isLongNotice, useGuidanceNotices } from '~/composables/useGuidanceNotices'
import { planAllows } from '~/composables/usePlanFeature'

const { t } = useI18n()
const { waitForAuth } = useAuth()
const { notices, load, dismiss } = useGuidanceNotices()

/** Ids the reader has expanded. Not persisted — it is a reading aid, not a preference. */
const expanded = ref<string[]>([])

function toggle(id: string) {
  expanded.value = expanded.value.includes(id)
    ? expanded.value.filter(x => x !== id)
    : [...expanded.value, id]
}

onMounted(async () => {
  await waitForAuth()
  // A member without a seat is refused the notices (`403 no_seat`, MAR-1212): do not ask.
  if (!(await planAllows('app'))) return
  await load()
})
</script>

<template>
  <div
      v-if="notices.length"
      class="shrink-0 border-b border-border bg-accent-lightest text-on-accent-lightest"
  >
    <div
        v-for="notice in notices"
        :key="notice.id"
        class="flex items-start gap-2.5 px-4 py-2 border-t border-border/50 first:border-t-0"
    >
      <Megaphone class="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground"/>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-medium leading-snug">{{ notice.title }}</p>
        <!-- v-text, not v-html: org-authored prose is untrusted input to every other member. -->
        <p
            v-if="notice.content"
            class="text-xs leading-relaxed text-muted-foreground whitespace-pre-line break-words"
            :class="expanded.includes(notice.id) ? '' : 'line-clamp-2'"
            v-text="notice.content"
        />
        <button
            v-if="isLongNotice(notice.content)"
            type="button"
            class="mt-0.5 text-[11px] font-medium text-primary hover:underline cursor-pointer"
            @click="toggle(notice.id)"
        >
          {{ expanded.includes(notice.id) ? t('common.showLess') : t('guidance.showMoreNotice') }}
        </button>
      </div>
      <button
          type="button"
          class="shrink-0 p-0.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors cursor-pointer"
          :title="t('guidance.dismissNotice')"
          :aria-label="t('guidance.dismissNotice')"
          @click="dismiss(notice.id)"
      >
        <X class="h-3.5 w-3.5"/>
      </button>
    </div>
  </div>
</template>
